import MarkdownIt from 'markdown-it';
import { sanitizeMarkdown } from './MarkdownSanitize.ts';

type Token = ReturnType<MarkdownIt['parse']>[number];

// Same parse options as vendors/markdown; its plugins only change rendering.
const md = new MarkdownIt({ html: true, linkify: true });

// A clamped summary only shows the start, so skip parsing the rest.
const MAX_SOURCE_LENGTH = 2000;

// Unclosed ones (e.g. cut by MAX_SOURCE_LENGTH) run to the end.
const HIDDEN_HTML_RE =
  /<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)|<!--[\s\S]*?(?:-->|$)/gi;
const HTML_TAG_RE = /<\/?([a-z][a-z0-9-]*)\b[^>]*>?/gi;

const LINE_BREAK_TAGS = new Set([
  'address',
  'article',
  'aside',
  'blockquote',
  'br',
  'dd',
  'div',
  'dl',
  'dt',
  'figcaption',
  'figure',
  'footer',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'header',
  'hr',
  'li',
  'main',
  'nav',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'tfoot',
  'thead',
  'tr',
  'ul'
]);
const CELL_TAGS = new Set(['td', 'th']);

const tagSeparator = (name: string) => {
  const tag = name.toLowerCase();
  if (LINE_BREAK_TAGS.has(tag)) return '\n';
  if (CELL_TAGS.has(tag)) return ' ';
  return '';
};

// unescapeAll also drops Markdown backslash escapes; keep backslashes literal.
const decodeEntities = (text: string) =>
  md.utils.unescapeAll(text.replace(/\\/g, '&#92;'));

const htmlToText = (html: string) =>
  decodeEntities(
    html
      .replace(HIDDEN_HTML_RE, '')
      .replace(/\s+/g, ' ')
      .replace(HTML_TAG_RE, (_, name: string) => tagSeparator(name))
  );

// Re-escape text so inline HTML and text go through one HTML pass.
const inlineToHtml = (children: Token[]) =>
  children
    .map(child => {
      switch (child.type) {
        case 'text':
        case 'code_inline':
          return md.utils.escapeHtml(child.content);
        case 'softbreak':
        case 'hardbreak':
          return '<br>';
        case 'html_inline':
          return child.content;
        default:
          return '';
      }
    })
    .join('');

/**
 * Plain-text preview of Markdown, one line per block, for places that clamp
 * the text instead of rendering it (e.g. card summaries).
 */
export function markdownToPlainText(markdown = ''): string {
  if (!markdown) return '';
  return md
    .parse(sanitizeMarkdown(markdown.slice(0, MAX_SOURCE_LENGTH)), {})
    .map(token => {
      if (token.type === 'inline') {
        return htmlToText(inlineToHtml(token.children ?? []));
      }
      if (token.type === 'html_block') return htmlToText(token.content);
      return '';
    })
    .flatMap(text => text.split('\n'))
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}
